import {AfterViewChecked, ChangeDetectorRef, Component, inject, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AuthControllerService, User, UserControllerService} from "../../api";
import {Subject} from "rxjs";
import {KeyValuePipe, NgTemplateOutlet} from "@angular/common";
import {MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef} from "@angular/material/dialog";
import {MatRipple} from "@angular/material/core";
import {AuthService} from "../../services/auth/auth.service";
import {PermissionService} from "../../services/permission/permission.service";
import {StateConfigService} from "../../services/state/state-config.service";
import {faCalendarDays, faChevronRight, faXmark} from "@fortawesome/pro-solid-svg-icons";
import {FtaPopupComponent} from "./fta-popup/fta-popup.component";
import {FtaPopupDeactivateComponent} from "./fta-popup/fta-popup-deactivate/fta-popup-deactivate.component";
import {ProfileImageUploadComponent} from "../util/profile-image-upload/profile-image-upload.component";
import {UserProjectsComponent} from "./user-projects/user-projects.component";
import {OrganisationListComponent} from "../organisations/components/organisationList/organisation-list.component";
import {BranchPipe} from "../../pipes/branch.pipe";
import {AddressInputComponent, applyDefaultCountry} from "../util/addressInput/address-input.component";
import {
  BicAccordionBodyComponent,
  BicAccordionComponent,
  BicAccordionElementComponent,
  BicAccordionHeaderComponent,
  BicButtonComponent,
  BicDatepickerInputDirective,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {NotificationsService} from "../../services/notifications/notifications.service";
import SalutationEnum = User.SalutationEnum;
import BranchEnum = User.BranchEnum;
import TitleEnum = User.TitleEnum;

/**
 * Profile of User
 */
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    ProfileImageUploadComponent,
    UserProjectsComponent,
    OrganisationListComponent,
    KeyValuePipe,
    BranchPipe,
    AddressInputComponent,
    RouterLink,
    BicSelectComponent,
    BicDropdownElementComponent,
    BicInputComponent,
    BicDatepickerInputDirective,
    BicButtonComponent,
    FaIconComponent,
    BicAccordionComponent,
    BicAccordionHeaderComponent,
    BicAccordionElementComponent,
    BicAccordionBodyComponent,
    PageHeaderComponent,
  ]
})
export class ProfileComponent implements OnInit, AfterViewChecked {
  permissionService = inject(PermissionService);
  dialog = inject(MatDialog);
  stateConfigService = inject(StateConfigService);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  private authControllerService = inject(AuthControllerService);
  private userControllerService = inject(UserControllerService);
  private cdr = inject(ChangeDetectorRef);
  private notificationsService = inject(NotificationsService);

  registerForm!: UntypedFormGroup;
  passwordForm!: UntypedFormGroup;
  addressInfoForm!: FormGroup;
  @ViewChild('accordion') accordion!: BicAccordionComponent;

  @Input() user!: User;

  tfaMail: boolean = false;
  tfaApp: boolean = false;

  showPasswordOld: boolean = false;
  showPasswordNew: boolean = false;
  showPasswordNewRepeat: boolean = false;

  unsubscribe: Subject<void> = new Subject<void>();
  salutation: typeof SalutationEnum = SalutationEnum;
  branch: typeof BranchEnum = BranchEnum;
  title: typeof TitleEnum = TitleEnum;

  isInit: boolean = false;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCalendarDays = faCalendarDays;
  /**
   * Initialization of Component
   */
  ngOnInit(): void {
    this.route.parent?.queryParams.subscribe(params => {
      this.isInit = params.init;
    });

    if (this.user) {
      this.initForm(this.user);
    } else {
      this.authControllerService.profile().subscribe(user => {
        this.user = user;
        this.initForm(user);
        if(this.user.isUsing2FA){
          if(this.user.isUsing2FAViaMail){
            this.tfaMail = true;
          }else{
            this.tfaApp = true;
          }
        }
        this.registerForm.updateValueAndValidity({onlySelf: false, emitEvent: true});
      });
    }

  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  /**
   * Init profile Form for given User
   * @param user user for Form Data
   */
  initForm(user: User) {
    this.registerForm = this.formBuilder.group({
      title: new UntypedFormControl(null),
      salutation: new UntypedFormControl(SalutationEnum.NONE, [Validators.required]),
      // firstName: new UntypedFormControl("", [Validators.required, Validators.pattern('^[a-zA-ZäöüÄÖÜß ]*$')]),
      // lastName: new UntypedFormControl("", [Validators.required, Validators.pattern('^[a-zA-ZäöüÄÖÜß ]*$')]),
      firstName: new UntypedFormControl("", [Validators.required, this.validateName()]),
      lastName: new UntypedFormControl("", [Validators.required, this.validateName()]),

      birthdate: new UntypedFormControl(),
      branch: new UntypedFormControl(),
      mail: new UntypedFormControl({value: this.user.mail, disabled: true}),
      phoneNumber: new UntypedFormControl(this.user.phoneNumber, [Validators.pattern('^\\+?\\d*$')]),
    });

    this.addressInfoForm = new FormGroup({
      zipCode: new UntypedFormControl(this.user.zipCode, [Validators.required, Validators.pattern('^[0-9]{4,5}$')]),
      city: new UntypedFormControl(this.user.city, [Validators.required]),
      country: new UntypedFormControl(this.user.country, [Validators.required]),
      street: new UntypedFormControl(this.user.street, [Validators.required]),
      houseNumber: new UntypedFormControl(this.user.houseNumber, [Validators.required]),
    });
    applyDefaultCountry(this.addressInfoForm.get('country'));


    this.passwordForm = this.formBuilder.group({
      currentPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [Validators.minLength(8), Validators.required, this.passwordValidator()]),
      newPasswordRepeat: new FormControl('', [Validators.required, this.matchPasswords()]),
      user: this.user
    });

    this.registerForm.patchValue(user);
    if (user.title === TitleEnum.NONE) {
      this.registerForm.get('title')?.setValue(null);
    }
    this.registerForm.patchValue({
      mailValidation: user.mail
    });

    let firstChange = true;
    this.registerForm.valueChanges.subscribe(() => {
      if(!firstChange) {
        this.registerForm.markAsDirty();
      }
      firstChange = false;
    });
    this.registerForm.updateValueAndValidity({ emitEvent: false });
  }
  protected readonly faXmark = faXmark;

  save(): void {
    // Prüfe ob Formular dirty und valid ist
    if (!this.registerForm.valid) {
      this.registerForm.markAllAsTouched();
      this.accordion.open(0);
      const isSalutationInvalid = this.registerForm.controls.salutation.invalid;

      if (isSalutationInvalid) {
        this.notificationsService.showErrorMessage("Bitte eine Anrede auswählen");
      } else if (this.registerForm.controls.firstName.errors?.['invalidName']) {
        this.notificationsService.showErrorMessage("Der Vorname muss mindestens 2 Zeichen lang sein");
      } else if (this.registerForm.controls.firstName.invalid) {
        this.notificationsService.showErrorMessage("Bitte einen Vornamen angeben");
      } else if (this.registerForm.controls.lastName.errors?.['invalidName']) {
        this.notificationsService.showErrorMessage("Der Nachname muss mindestens 2 Zeichen lang sein");
      } else if (this.registerForm.controls.lastName.invalid) {
        this.notificationsService.showErrorMessage("Bitte einen Nachnamen angeben");
      } else if (this.registerForm.controls.phoneNumber.invalid) {
        this.notificationsService.showErrorMessage("Bitte eine gültige Telefonnummer eingeben (nur Zahlen und optional ein + am Anfang)");
      } else {
        this.notificationsService.showErrorMessage("Bitte alle Pflichtfelder ausfüllen");
      }
      return;
    }

    // Nur speichern wenn etwas geändert wurde
    if (!this.registerForm.dirty && !this.isInit) {
      this.notificationsService.showInfoMessage("Keine Änderungen zu speichern");
      return;
    }

    const formValueRaw = {
      ...this.registerForm.value,
      phoneNumber: this.registerForm.value.phoneNumber ?? null
    };
    const formValue = {
      ...formValueRaw,
      title: formValueRaw.title == null ? TitleEnum.NONE : formValueRaw.title
    };
    this.user = {...this.user, ...formValue};

    this.userControllerService.updateUser(this.user).subscribe({
      next: (updatedUser) => {
        this.registerForm.markAsPristine();
        this.user = updatedUser;
        this.notificationsService.showSuccessMessage("Änderungen gespeichert");
        if (this.isInit) {
          this.accordion.open(1);
        }
      },
      error: (error) => {
        this.notificationsService.showErrorMessage("Fehler beim Speichern: " + (error?.error?.message || "Unbekannter Fehler"));
      }
    });
  }

  canDeactivate(): boolean {
    return this.registerForm.valid && this.addressInfoForm.valid && (this.addressInfoForm.pristine || this.isInit) && (this.registerForm.pristine || this.isInit);
  }

  validForm(): boolean {
    return this.registerForm.valid && this.addressInfoForm.valid;
  }

  disableForm(): boolean {
    return this.registerForm.pristine && this.addressInfoForm.pristine;
  }

  saveAddress(): void {
    // Prüfe ob Formular valid ist
    if (!this.addressInfoForm.valid) {
      this.addressInfoForm.markAllAsTouched();
      this.accordion.open(1);

      if (this.addressInfoForm.controls['zipCode'].invalid) {
        this.notificationsService.showErrorMessage("Bitte eine gültige Postleitzahl angeben (4-5 Ziffern)");
      } else if (this.addressInfoForm.controls['street'].invalid) {
        this.notificationsService.showErrorMessage("Bitte eine Straße angeben");
      } else if (this.addressInfoForm.controls['houseNumber'].invalid) {
        this.notificationsService.showErrorMessage("Bitte eine Hausnummer angeben");
      } else if (this.addressInfoForm.controls['city'].invalid) {
        this.notificationsService.showErrorMessage("Bitte einen Ort angeben");
      } else if (this.addressInfoForm.controls['country'].invalid) {
        this.notificationsService.showErrorMessage("Bitte ein Land angeben");
      } else {
        this.notificationsService.showErrorMessage("Bitte alle Pflichtfelder ausfüllen");
      }
      return;
    }

    // Nur speichern wenn etwas geändert wurde
    if (!this.addressInfoForm.dirty && !this.isInit) {
      this.notificationsService.showInfoMessage("Keine Änderungen zu speichern");
      return;
    }

    this.user = {...this.user, ...this.addressInfoForm.value};

    this.userControllerService.updateUser(this.user).subscribe({
      next: (updatedUser) => {
        this.addressInfoForm.markAsPristine();
        this.user = updatedUser;
        this.notificationsService.showSuccessMessage("Änderungen gespeichert");
        if (this.isInit) {
          this.accordion.open(2);
        }
      },
      error: (error) => {
        this.notificationsService.showErrorMessage("Fehler beim Speichern: " + (error?.error?.message || "Unbekannter Fehler"));
      }
    });
  }

  renewPassword(): void {
    // Prüfe ob Formular valid ist
    if (!this.passwordForm.valid) {
      this.notificationsService.showErrorMessage("Bitte alle Pflichtfelder ausfüllen und Anforderungen erfüllen");
      return;
    }

    // Prüfe ob die Passwörter übereinstimmen
    if (this.passwordForm.value.newPassword !== this.passwordForm.value.newPasswordRepeat) {
      this.notificationsService.showErrorMessage("Passwörter stimmen nicht überein");
      return;
    }

    this.authControllerService.renewPassword(this.passwordForm.value).subscribe({
      next: () => {
        this.passwordForm = this.formBuilder.group({
          currentPassword: new UntypedFormControl(),
          newPassword: new UntypedFormControl(),
          newPasswordRepeat: new UntypedFormControl(),
          user: this.user
        });
        this.notificationsService.showSuccessMessage("Passwort erfolgreich geändert");
      },
      error: (error) => {
        this.notificationsService.showErrorMessage("Passwort konnte nicht geändert werden: " + (error?.error?.message || "Unbekannter Fehler"));
      }
    });
  }

  /**
   * Opens User delete Dialog
   */
  openDeleteDialog() {
    this.dialog.open(UserDeleteDialog, {
      hasBackdrop: false,
      data: {user: this.user}
    });
  }

  handleChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    if (inputElement.id == 'tfaApp') {
      this.tfaApp = inputElement.checked;
      if (this.tfaMail) {
        this.tfaMail = false;
        this.openDeactivatePopup('tfaApp', true);
      } else {
        if (!this.tfaApp) {
          this.openDeactivatePopup('tfaApp');
        } else {
          this.openTfaPopup();
        }
      }
    } else {
      this.tfaMail = inputElement.checked;
      if (this.tfaApp) {
        this.tfaApp = false;
        this.openDeactivatePopup('tfaMail', true);
      } else {
        if (!this.tfaMail) {
          this.openDeactivatePopup('tfaMail');
        } else {
          this.openTfaPopup();
        }
      }
    }
  }

  openDeactivatePopup(input: string, changeMethode: boolean = false) {
    const dialogRef = this.dialog.open(FtaPopupDeactivateComponent, {
      hasBackdrop: false,
      data: {changeMethode: changeMethode, user: this.user, methode: input},
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result.success) {
        if (result.activateOtherMethod) {
          if (input == 'tfaApp') {
            this.tfaApp = true;
            this.openTfaPopup();
          } else {
            this.tfaMail = true;
            this.openTfaPopup();
          }
        } else {
          this.notificationsService.showSuccessMessage("2FA erfolgreich deaktiviert");
          this.tfaApp = false;
          this.tfaMail = false;
        }
      } else {
        if (changeMethode) {
          if (input == 'tfaApp') {
            this.tfaMail = true;
            this.tfaApp = false;
          } else {
            this.tfaApp = true;
            this.tfaMail = false;
          }
        } else {
          if (input == 'tfaApp') {
            this.tfaApp = true;
          } else {
            this.tfaMail = true;
          }
        }
      }
    });
  }

  testLength(str: string): boolean {
    return str.length >= 8;
  }

  testLowerCase(str: string): boolean {
    return /[a-z]+/.test(str);
  }

  testUpperCase(str: string): boolean {
    return /[A-Z]+/.test(str);
  }

  testNumeric(str: string): boolean {
    return /[0-9]+/.test(str);
  }

  openTfaPopup() {
    const dialogRef = this.dialog.open(FtaPopupComponent, {
      data: {isMail: this.tfaMail, user: this.user},
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result.success) {
        this.notificationsService.showSuccessMessage("2FA erfolgreich aktiviert");
        this.authControllerService.profile().subscribe((user: User) => {
          this.user = user;
        });
      } else {
        this.tfaApp = false;
        this.tfaMail = false;
      }
    });
  }

  matchPasswords(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
      if (this.passwordForm) {
        return this.passwordForm.get('newPassword')?.value == control.value ? null : {matchValue: false};
      } else {
        return {matchValue: false};
      }

    };
  }

  testSpecialCharacter(str: string): boolean {
    return /[!?&%=.\-:*#@$\\]+/.test(str);
  }

  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]+/.test(value);
      const hasLowerCase = /[a-z]+/.test(value);
      const hasNumeric = /[0-9]+/.test(value);
      const hasSpecialCharacter = /[!?&%=.\-:*#@$\\]+/.test(value);
      const hasNoSpaces = !/\s/.test(value);


      const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialCharacter && hasNoSpaces;

      return !passwordValid ? {passwordInvalid: true} : null;
    };
  }

  validateName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';
      if (!value) {
        return null;
      }
      return value.trim().length >= 2 ? null : { invalidName: true };
    };
  }

}

/**
 * Dialog for User Deletion
 */
@Component({
  selector: 'profile-deleteDialog',
  templateUrl: './profile-delete-dialog.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    MatDialogClose
  ]
})
export class UserDeleteDialog implements OnInit {
  data = inject<{
    user: User;
  }>(MAT_DIALOG_DATA);
  private router = inject(Router);
  private userControllerService = inject(UserControllerService);
  private dialogRef = inject<MatDialogRef<UserDeleteDialog>>(MatDialogRef);
  private authService = inject(AuthService);

  @ViewChild(MatRipple) ripple!: MatRipple;

  protected readonly faXmark = faXmark;

  close() {
    this.dialogRef.close();
  }

  delete() {
    this.userControllerService.deleteUser(this.data.user.id!).subscribe(() => {
      this.authService.logout();
      this.dialogRef.close();
      this.router.navigate(
        ["/login"],
        {queryParams: {isDeletedAccount: true}}
      );
    });
  }

  /**
   * Initialization of Component
   */
  ngOnInit() {
  }
}
